import {
  startOidcLogin,
  fetchOidcLogoutUrl,
} from "@/services/api-service/auth/oidc-api";
import { clearStorage } from "@/services/storage-data";

/** Paths we must never bounce back to after login (avoids redirect loops / dead ends). */
const UNSAFE_RETURN_PREFIXES = [
  "//",
  "/login",
  "/gomake-ai",
  "/admin/login",
  "/auth/oidc",
  "/token-auth",
  "/no-print-house",
  "/404",
];

/** Returns a safe in-app path to land on after login, or undefined to use the default. */
export const getSafeReturnPath = (
  path?: string | null,
): string | undefined => {
  if (!path || !path.startsWith("/")) return undefined;
  if (UNSAFE_RETURN_PREFIXES.some((prefix) => path.startsWith(prefix))) {
    return undefined;
  }
  return path;
};

/**
 * Sends the browser to the Cognito Hosted UI (generic, all providers). Captures the page the
 * user was trying to reach and the host they started on so the callback can route them back.
 *
 * @param returnPath Optional explicit path to return to; defaults to the current path + query.
 */
export const startHostedLogin = async (returnPath?: string): Promise<void> => {
  if (typeof window === "undefined") return;

  const currentPath = `${window.location.pathname}${window.location.search}`;
  const returnUrl = getSafeReturnPath(returnPath ?? currentPath);
  const originHost = window.location.host;

  const authorizeUrl = await startOidcLogin({ returnUrl, originHost });
  if (authorizeUrl) {
    window.location.href = authorizeUrl;
    return;
  }
  throw new Error("Failed to start login. Please try again.");
};

/**
 * Logs the user out of both GoMake and Cognito. Clears local tokens, then redirects to the
 * Cognito Hosted UI sign-out endpoint so Cognito's own session is ended too — otherwise the
 * Hosted UI would silently re-authenticate the user right after we send them back to /login.
 * Cognito ends the session, immediately re-shows the login screen, and (after sign-in) returns
 * the browser through the normal callback to `returnPath` when provided.
 *
 * @param returnPath Optional in-app path to land on after signing back in.
 */
export const startHostedLogout = async (returnPath?: string): Promise<void> => {
  clearStorage();
  if (typeof window === "undefined") return;

  const originHost = window.location.host;
  const returnUrl = getSafeReturnPath(returnPath);
  try {
    const logoutUrl = await fetchOidcLogoutUrl({ originHost, returnUrl });
    if (logoutUrl) {
      window.location.href = logoutUrl;
      return;
    }
  } catch {
    // Fall through to a local-only logout if the logout URL can't be built.
  }
  // Fallback: at least return to the login page locally.
  window.location.href = `${window.location.origin}/login`;
};
